import { AbstractControl, FormArray } from '@angular/forms';
import { ProductSection, ProductSummary } from 'src/app/model/product';
import { extractErrorMessage } from 'src/app/service/admin-error';

export type PhotoFormValue = { photo?: string; file?: File } | string | null | undefined;
export type PhotoUploader = (file: File) => Promise<string>;

const inFlightUploads = new WeakMap<File, Map<PhotoUploader, Promise<string>>>();

export function isImageDataUrl(value: unknown): boolean {
  return typeof value === 'string' && value.startsWith('data:image');
}

/**
 * 폼 사진 값(저장된 URL 문자열 또는 `{photo, file}`)을 미리보기가 읽는 URL로 맞춘다.
 * 미리보기의 AI 보정이 사진 순번으로 폼에 되쓰므로 빈 값도 걸러내지 않고 자리를 지킨다.
 */
export function toPreviewPhotoUrls(photos: PhotoFormValue[] | null | undefined): string[] {
  return (photos ?? []).map((photo) => {
    const url = typeof photo === 'string' ? photo : photo?.photo;
    return typeof url === 'string' ? url : '';
  });
}

/**
 * 저장 DTO에 넣을 사진 한 장. null·빈 칸만 undefined 로 돌려 호출부가
 * 건너뛰게 하고, 모르는 모양의 truthy 값(레거시 {url}·숫자 id 등)은
 * 그대로 돌려 백엔드 검증에서 시끄럽게 실패하게 한다 — 조용히 버리면
 * 사진이 사라진 채 저장이 성공한 것처럼 보인다.
 */
export function storedPhotoValue(photo: unknown): unknown {
  if (photo == null || photo === '') return undefined;
  if (typeof photo !== 'object') return photo;
  const inner = (photo as { photo?: unknown }).photo;
  if ('photo' in photo) return inner == null || inner === '' ? undefined : inner;
  return photo;
}

export function toPreviewSummaries(summaries: ProductSummary[] | null | undefined): ProductSummary[] | undefined {
  return summaries?.map((summary) => ({ ...summary, photos: toPreviewPhotoUrls(summary.photos) }));
}

export function toPreviewSections(sections: ProductSection[] | null | undefined): ProductSection[] | undefined {
  return sections?.map((section) => ({
    ...section,
    paragraphs: section.paragraphs?.map((paragraph) => ({
      ...paragraph,
      photos: toPreviewPhotoUrls(paragraph.photos),
    })),
  }));
}

/**
 * 되돌리기가 폼에 써 넣은 data URL까지 저장 전에 S3로 올린다.
 * 어드민 저장 경로는 옛 데이터의 알 수 없는 값도 그대로 보존해야 해서 `toStoredPhotoUrl`처럼 거르지 않는다.
 */
export async function toAdminStoredPhotoUrl(photo: PhotoFormValue, upload: PhotoUploader): Promise<string> {
  const value = typeof photo === 'string' ? photo : photo?.photo;
  if (typeof photo === 'object' && photo?.file) return upload(photo.file);
  if (isImageDataUrl(value)) {
    const blob = await fetch(value).then((r) => r.blob());
    return upload(new File([blob], `ai-enhanced-${Date.now()}.png`, { type: blob.type }));
  }
  return (value ?? photo) as string;
}

/**
 * 오버레이가 맡은 사진이 바뀌었는지.
 * 보정 중에는 히스토리가 아직 비어 있으므로 지금 들고 있는 URL까지 함께 본다.
 */
export function ownsAnotherPhoto(currentUrl: string | undefined, history: string[], nextUrl: string): boolean {
  if (nextUrl === currentUrl) return false;
  return !history.includes(nextUrl);
}

/** 보정이 끝난 사이 목록이 바뀌었으면 그 자리는 이미 다른 사진이다. */
export function isExpectedPhotoTarget(current: PhotoFormValue, expectedUrl?: string): boolean {
  if (!expectedUrl) return true;
  const url = typeof current === 'string' ? current : current?.photo;
  return url === expectedUrl;
}

function uploadOnce(file: File, upload: PhotoUploader): Promise<string> {
  let byUploader = inFlightUploads.get(file);
  if (!byUploader) {
    byUploader = new Map();
    inFlightUploads.set(file, byUploader);
  }
  let pending = byUploader.get(upload);
  if (!pending) {
    pending = upload(file).catch((error) => {
      byUploader.delete(upload);
      throw error;
    });
    byUploader.set(upload, pending);
  }
  return pending;
}

/**
 * 저장할 사진 URL을 돌려준다. 업로드한 값은 폼 값 객체에 되써서 다음 저장이 같은 파일을 다시 올리지 않게 한다.
 * data URL·http(s)가 아닌 값은 그대로 저장하면 TEXT 컬럼에서 잘리거나 깨진 이미지가 되므로 통과시키지 않는다.
 */
export async function toStoredPhotoUrl(photo: PhotoFormValue, upload: PhotoUploader): Promise<string | null> {
  const value = typeof photo === 'string' ? { photo } : photo;
  if (!value?.file && !value?.photo) return null;

  let url: string;
  if (value.file) {
    url = await uploadOnce(value.file, upload);
  } else if (isImageDataUrl(value.photo)) {
    const blob = await fetch(value.photo).then((r) => r.blob());
    url = await upload(new File([blob], 'embedded-image', { type: blob.type }));
  } else if (/^https?:\/\//.test(value.photo)) {
    return value.photo;
  } else {
    throw new Error(`저장할 수 없는 사진 값입니다 (${value.photo.slice(0, 30)})`);
  }

  if (typeof photo === 'object') {
    photo.photo = url;
    delete photo.file;
  }
  return url;
}

export async function toStoredPhotoUrls(
  photos: PhotoFormValue[],
  upload: PhotoUploader,
  label: string,
): Promise<string[]> {
  const urls: string[] = [];
  for (const [i, photo] of photos.entries()) {
    try {
      const url = await toStoredPhotoUrl(photo, upload);
      if (url) urls.push(url);
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      throw new Error(`${label} ${i + 1}번째 사진을 올리지 못했습니다: ${reason}`);
    }
  }
  return urls;
}

export const PHOTO_MOVED_MESSAGE = '편집하는 동안 사진이 바뀌어 AI 편집 결과를 적용하지 않았습니다. 다시 시도해주세요.';
export const PHOTO_GONE_MESSAGE = '미리보기에서 사진이 사라져 AI 보정 결과를 적용하지 않았습니다.';

export function controlAt(
  array: AbstractControl | null | undefined,
  index: number | undefined,
): AbstractControl | undefined {
  if (!(array instanceof FormArray) || index === undefined || index < 0 || index >= array.length) return undefined;
  return array.at(index);
}

export function locateSectionPhotoError(
  sectionIndex: number,
  paragraphIndex: number,
  photoIndex: number,
  sectionTitle: unknown,
  error: unknown,
): Error {
  const title = typeof sectionTitle === 'string' ? sectionTitle.trim() : '';
  const titlePart = title ? ` 「${title}」` : '';
  return photoUploadError(
    `섹션 ${sectionIndex + 1}${titlePart} · 문단 ${paragraphIndex + 1} · 사진 ${photoIndex + 1}`,
    error,
  );
}

// cause 를 달아야 Sentry linkedErrors 가 원본 예외의 스택을 붙이고, 실패 원인별로 이슈가 갈린다.
export function photoUploadError(location: string, error: unknown): Error {
  return new Error(`${location}: ${extractErrorMessage(error, String(error))}`, { cause: error });
}
